/**
 * Per-driver scan ports for the Connect wizard's Discover step: the discover
 * body's `ports` (driver id => the TCP ports to probe, replacing that driver's
 * defaults; forge-protocol v0.3.0, platform-core `DiscoverPayload.Ports`).
 *
 * Each probed driver gets a "Ports" field prefilled with the ports the gateway
 * probes by default. Only a list the operator changed is sent, so a scan left
 * at the defaults is byte-identical to the pre-ports request and an old
 * gateway (which ignores `ports`) behaves the same. For modbus-tcp a single
 * changed port also goes out as the legacy `port`, which gateways that predate
 * `ports` still honour; the API requires the two to agree.
 */

import type { DriverInfo } from "@/lib/api";
import { DRIVER, driverDefaultPort } from "./drivers";

/** At most this many ports per driver in one scan (protocol.DiscoverMaxPortsPerDriver). */
export const MAX_SCAN_PORTS = 4;

/**
 * The ports a gateway probes for each driver when none are given (GatewayPro
 * internal/discovery: MELSEC tries its usual MC-protocol ports in turn). The
 * drivers list only carries one `default_port`, so the lists live here.
 */
const DISCOVER_PORTS: Record<string, readonly number[]> = {
  [DRIVER.MODBUS_TCP]: [502],
  [DRIVER.OPCUA]: [4840],
  [DRIVER.S7]: [102],
  [DRIVER.ENIP]: [44818],
  [DRIVER.MELSEC]: [5007, 5000, 5010, 1024, 1026],
};

/** The ports a scan probes for a driver by default: the known list, else the drivers list's port. */
export function defaultScanPorts(id: string, drivers?: DriverInfo[] | null): number[] {
  const known = DISCOVER_PORTS[id];
  if (known) return [...known];
  const p = driverDefaultPort(id, drivers);
  return p ? [p] : [];
}

/** A port list as the field shows it: "5007, 5000". */
export function formatScanPorts(ports: readonly number[]): string {
  return ports.join(", ");
}

export type ScanPortsCheck =
  | { ok: true; ports: number[]; isDefault: boolean }
  | { ok: false; error: string };

const sameSet = (a: readonly number[], b: readonly number[]) =>
  a.length === b.length && a.every((p) => b.includes(p));

/**
 * A typed "Ports" field: ports separated by commas (or spaces). Blank, or the
 * defaults in any order, means the defaults (nothing is sent). A changed list
 * needs 1 to {@link MAX_SCAN_PORTS} distinct whole numbers from 1 to 65535.
 */
export function checkScanPorts(text: string, defaults: readonly number[]): ScanPortsCheck {
  const parts = text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter((s) => s !== "");
  if (parts.length === 0) return { ok: true, ports: [...defaults], isDefault: true };
  const ports: number[] = [];
  for (const s of parts) {
    const n = /^\d+$/.test(s) ? Number(s) : NaN;
    if (!Number.isInteger(n) || n < 1 || n > 65535) {
      return { ok: false, error: `“${s}” is not a port. A port is a whole number from 1 to 65535.` };
    }
    if (ports.includes(n)) return { ok: false, error: `Port ${n} is listed twice.` };
    ports.push(n);
  }
  if (sameSet(ports, defaults)) return { ok: true, ports, isDefault: true };
  if (ports.length > MAX_SCAN_PORTS) {
    return { ok: false, error: `Give at most ${MAX_SCAN_PORTS} ports, separated by commas.` };
  }
  return { ok: true, ports, isDefault: false };
}

/** The discover body's port fields: `ports` for the changed drivers, and the legacy modbus `port`. */
export interface DiscoverPortsParam {
  ports?: Record<string, number[]>;
  port?: number;
}

/**
 * The `ports` (and legacy `port`) to send for the probed drivers, from each
 * driver's field text (missing = untouched = the defaults). Unchanged and
 * invalid fields send nothing; the panel blocks the scan on an invalid one.
 */
export function discoverPortsParam(
  probed: readonly string[],
  texts: Readonly<Record<string, string | undefined>>,
  drivers?: DriverInfo[] | null,
): DiscoverPortsParam {
  const ports: Record<string, number[]> = {};
  for (const id of probed) {
    const text = texts[id];
    if (text === undefined) continue;
    const c = checkScanPorts(text, defaultScanPorts(id, drivers));
    if (c.ok && !c.isDefault) ports[id] = c.ports;
  }
  const out: DiscoverPortsParam = {};
  if (Object.keys(ports).length > 0) out.ports = ports;
  const mb = ports[DRIVER.MODBUS_TCP];
  if (mb && mb.length === 1) out.port = mb[0];
  return out;
}
